import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { toPng } from 'html-to-image';
import moment from 'moment';
import { DeliveryPlanViewerRouteStop, DeliveryPlanViewerShift, StopSelection } from '../delivery-plan-viewer.models';
import {
    DELIVERY_COLOR,
    EXPORT_BACKGROUND_COLOR,
    EXPORT_FILE_PREFIX,
    EXPORT_PIXEL_RATIO,
    PICKUP_COLOR,
    SHEET_ANIMATION,
    SHEET_COLLAPSED_SIZE,
    SHEET_EXPANDED_SIZE,
    SHEET_TAP_THRESHOLD_PX,
    STOP_TYPE_PICKUP,
    TIME_FORMAT,
} from '../delivery-plan-viewer.constants';
import { InlineToastService } from 'shared';

interface StopsPanelShift
{
    /** Unique key for *ngFor. */
    key: string;
    labelParams: { number: number; start: string; end: string };
    stops: StopsPanelStop[];
}

interface StopsPanelStop
{
    number: number;
    stop: DeliveryPlanViewerRouteStop;
    typeLabelKey: string;
    color: string;
    eta: string;
}

@Component({
    selector: 'app-delivery-plan-viewer-stops-panel',
    templateUrl: './delivery-plan-viewer-stops-panel.component.html',
    styleUrls: ['./delivery-plan-viewer-stops-panel.component.scss'],
})
export class DeliveryPlanViewerStopsPanelComponent implements OnChanges, AfterViewInit, OnDestroy
{
    @Input() public shifts: DeliveryPlanViewerShift[] = [];
    @Input() public color = "";
    @Input() public courierLabel = "";
    @Input() public selectedStopId: string | null = null;

    @HostBinding("class.is-sheet")
    @Input()
    public asBottomSheet = false;

    @Output() public selectStop = new EventEmitter<StopSelection | null>();

    @ViewChild("sheetHandle", { static: true })
    private sheetHandle: ElementRef<HTMLElement>;

    @ViewChild("exportContent")
    private exportContent?: ElementRef<HTMLElement>;

    public panelShifts: StopsPanelShift[] = [];

    public isExporting = false;

    private sheetSize = SHEET_COLLAPSED_SIZE;

    private removeSheetListeners: (() => void) | null = null;

    constructor(
        private host: ElementRef<HTMLElement>,
        private zone: NgZone,
        private changeDetector: ChangeDetectorRef,
        private toast: InlineToastService
    ) {}

    public ngOnChanges(changes: SimpleChanges): void
    {
        if (changes['shifts'])
            this.panelShifts = this.buildShifts();

        if (changes['asBottomSheet'] && !changes['asBottomSheet'].firstChange)
            this.applySheetSize(false);
    }

    public ngAfterViewInit(): void
    {
        this.applySheetSize(false);
        this.zone.runOutsideAngular(() => this.listenToSheetDrag());
    }

    public ngOnDestroy(): void
    {
        this.removeSheetListeners?.();
    }

    public onStopClicked(stop: DeliveryPlanViewerRouteStop): void
    {
        this.selectStop.emit(
            stop.id === this.selectedStopId
                ? null
                : { stopId: stop.id, lat: stop.lat, lng: stop.lng }
        );
    }

    public async exportAsImage(): Promise<void>
    {
        if (this.isExporting)
            return;
 
        this.isExporting = true;

        // Renders the off-screen export copy before it is captured.
        this.changeDetector.detectChanges();

        try
        {
            if (!this.exportContent)
                return;

            const dataUrl = await toPng(this.exportContent.nativeElement, {
                pixelRatio: EXPORT_PIXEL_RATIO,
                backgroundColor: EXPORT_BACKGROUND_COLOR,
                // Embedding every web font of the app would be slow and can fail on cross-origin
                // stylesheets; the export uses a system font stack instead (see the scss).
                skipFonts: true,
            });
            const safeLabel = this.courierLabel.replace(/[^A-Za-z0-9]+/g, '_');
            this.downloadDataUrl(dataUrl, `${EXPORT_FILE_PREFIX}${safeLabel}.png`);
        }
        catch (error)
        {
            this.toast.show("delivery-plan-viewer.image-export-failed");
        }
        finally
        {
            this.isExporting = false;
            this.changeDetector.detectChanges();
        }
    }

    public trackByKey(_index: number, shift: StopsPanelShift): string
    {
        return shift.key;
    }

    public trackByStopId(_index: number, panelStop: StopsPanelStop): string
    {
        return panelStop.stop.id;
    }

    private downloadDataUrl(dataUrl: string, filename: string): void
    {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = filename;
        link.click();
    }

    private buildShifts(): StopsPanelShift[]
    {
        return this.shifts.map((shift, shiftIndex) => ({
            key: `${shift.id}-${shiftIndex}`,
            labelParams: {
                number: shiftIndex + 1,
                start: this.formatTime(shift.start),
                end: this.formatTime(shift.end),
            },
            stops: shift.stops.map((stop) => {
                const isPickup = stop.stopType.toLowerCase() === STOP_TYPE_PICKUP;
                return {
                    number: stop.stopNumber,
                    stop,
                    typeLabelKey: isPickup
                        ? "delivery-plan-viewer.stop-type-pickup"
                        : "delivery-plan-viewer.stop-type-delivery",
                    color: isPickup ? PICKUP_COLOR : DELIVERY_COLOR,
                    eta: this.formatTime(stop.eta),
                };
            }),
        }));
    }

    private formatTime(date: Date): string
    {
        return moment(date).format(TIME_FORMAT);
    }

    private applySheetSize(animate: boolean): void
    {
        const style = this.host.nativeElement.style;
        if (!this.asBottomSheet)
        {
            style.height = "";
            style.transition = "";
            return;
        }
        style.transition = animate ? SHEET_ANIMATION : "none";
        style.height = `${this.sheetSize * 100}%`;
    }

    private snapSheet(toggle: boolean): void
    {
        const midpoint = (SHEET_COLLAPSED_SIZE + SHEET_EXPANDED_SIZE) / 2;
        const isOpen = this.sheetSize >= midpoint;
        this.sheetSize = isOpen !== toggle ? SHEET_EXPANDED_SIZE : SHEET_COLLAPSED_SIZE;
        this.applySheetSize(true);
    }

    private listenToSheetDrag(): void
    {
        const handle = this.sheetHandle.nativeElement;
        let startY = 0;
        let startSize = 0;
        let parentHeight = 1;
        let movedPx = 0;
        let pointerId: number | null = null;

        const onPointerDown = (event: PointerEvent) =>
            {
            if (!this.asBottomSheet || (event.target as HTMLElement).closest("button"))
                return;

            pointerId = event.pointerId;
            startY = event.clientY;
            startSize = this.sheetSize;
            parentHeight = this.host.nativeElement.parentElement?.clientHeight || 1;
            movedPx = 0;
            handle.setPointerCapture(event.pointerId);
        };

        const onPointerMove = (event: PointerEvent) =>
        {
            if (event.pointerId !== pointerId)
                return;

            const deltaY = event.clientY - startY;
            movedPx = Math.max(movedPx, Math.abs(deltaY));
            this.sheetSize = Math.min(
                SHEET_EXPANDED_SIZE,
                Math.max(SHEET_COLLAPSED_SIZE, startSize - deltaY / parentHeight)
            );
            this.applySheetSize(false);
        };

        const onPointerUp = (event: PointerEvent) =>
        {
            if (event.pointerId !== pointerId)
                return;

            pointerId = null;
            this.snapSheet(movedPx < SHEET_TAP_THRESHOLD_PX);
        };

        handle.addEventListener("pointerdown", onPointerDown);
        handle.addEventListener("pointermove", onPointerMove);
        handle.addEventListener("pointerup", onPointerUp);
        handle.addEventListener("pointercancel", onPointerUp);

        this.removeSheetListeners = () =>
        {
            handle.removeEventListener("pointerdown", onPointerDown);
            handle.removeEventListener("pointermove", onPointerMove);
            handle.removeEventListener("pointerup", onPointerUp);
            handle.removeEventListener("pointercancel", onPointerUp);
        };
    }

}